// @flow
import React from "react";
import { useSelector } from "react-redux";

import { Translate } from "@quantum/i18n";

import {
  SelecaoCasasDecimais,
  SelecaoTipoConta,
  SelecaoVersaoConta,
  SelecaoFormaAcumulacao,
  SelecaoPeriodoIndicadoresFinanceiros,
  SelecaoDeflacao,
  SelecaoToleranciaDF,
  SelecaoTipoCalculo,
} from "../configuracoes";
import {
  comGeradorDePeriodo,
  getEdicaoMedida,
  getDataBaseSelecionadaAsString,
  isModoSeries,
  getTipoCalculoSelecionado,
  getComparacaoSelecionada,
  analiseValorDaMedida,
} from "../../../state";
import SelecaoFrequencia from "../configuracoes/periodos/indicadoresFinanceiros/SelecaoFrequencia";
import SelecaoComparacaoAnaliseHorizontal from "../configuracoes/periodos/indicadoresFinanceiros/SelecaoComparacaoAnaliseHorizontal";
import {
  ID_TIPO_CALCULO_PONTUAL,
  ID_TIPO_CALCULO_VARIACAO_PERCENTUAL,
} from "../../../model";

type Props = {
  exibirTipoCalculo: boolean,
};

export const processarAcumulacaoDeflator = comGeradorDePeriodo(
  (state, medidas) => {
    const edicaoMedida = getEdicaoMedida(state);
    const dataBase = getDataBaseSelecionadaAsString(state);
    const modoSeries = isModoSeries(state);
    const comparacao = getComparacaoSelecionada(state);

    if (modoSeries) {
      return medidas.map(medidaSelecionada => ({
        ...medidaSelecionada,
        tipoConta: edicaoMedida.tipoConta,
        versaoConta: edicaoMedida.versaoConta,
        formaAcumulacao: edicaoMedida.formaAcumulacao,
        toleranciaDF: edicaoMedida.toleranciaDF,
        deflator: edicaoMedida.deflator,
        valorBaseDeflator: edicaoMedida.valorBaseDeflator,
        valorBaseDeflatorOutro: edicaoMedida.valorBaseDeflatorOutro,
        dataBaseDeflator: edicaoMedida.dataBaseDeflator,
        dataBaseDeflatorDataEspecifica:
          edicaoMedida.dataBaseDeflatorDataEspecifica,
        dataBase: dataBase,
        ajusteDias: edicaoMedida.ajusteDias,
        frequencias: edicaoMedida.frequencias,
        dataReferencia: comparacao,
        tipoCalculoAnaliseHorizontal: edicaoMedida.tipoCalculo,
        ...analiseValorDaMedida(medidaSelecionada, state),
      }));
    } else {
      return medidas.map(medidaSelecionada => ({
        ...medidaSelecionada,
        tipoConta: edicaoMedida.tipoConta,
        versaoConta: edicaoMedida.versaoConta,
        formaAcumulacao: edicaoMedida.formaAcumulacao,
        toleranciaDF: edicaoMedida.toleranciaDF,
        deflator: edicaoMedida.deflator,
        valorBaseDeflator: edicaoMedida.valorBaseDeflator,
        valorBaseDeflatorOutro: edicaoMedida.valorBaseDeflatorOutro,
        dataBaseDeflator: edicaoMedida.dataBaseDeflator,
        dataBaseDeflatorDataEspecifica:
          edicaoMedida.dataBaseDeflatorDataEspecifica,
        dataBase: dataBase,
        ajusteDias: edicaoMedida.ajusteDias,
        ...analiseValorDaMedida(medidaSelecionada, state),
      }));
    }
  }
);

export default function AcumulacaoDeflator({ exibirTipoCalculo }: Props) {
  const modoSeries = useSelector(isModoSeries);
  const calculoSelecionado = useSelector(getTipoCalculoSelecionado);

  if (modoSeries) {
    return (
      <>
        <div className="ConfiguracaoMedida__box">
          <div className="ConfiguracaoMedida__box">
            <SelecaoToleranciaDF />
          </div>

          <div className="ConfiguracaoMedida__box">
            {exibirTipoCalculo && <SelecaoTipoCalculo />}
          </div>

          <div className="ConfiguracaoMedida__box">
            <SelecaoFrequencia />
          </div>

          {calculoSelecionado === ID_TIPO_CALCULO_VARIACAO_PERCENTUAL ? (
            <div className="ConfiguracaoMedida__box">
              <div className="ConfiguracaoMedida">
                <SelecaoComparacaoAnaliseHorizontal />
              </div>
            </div>
          ) : null}
        </div>

        <div className="ConfiguracaoMedida__vertical-divider" />

        <div className="ConfiguracaoMedida">
          <h3 className="ConfiguracaoMedida__group-title">
            <Translate>configuracaoRelatorio.parametros</Translate>
          </h3>
          <div className="ConfiguracaoMedida__group--grey">
            <div className="ConfiguracaoMedida__box">
              <SelecaoTipoConta />
            </div>
            <div className="ConfiguracaoMedida__box">
              <SelecaoVersaoConta />
            </div>
            <div className="ConfiguracaoMedida__box">
              <SelecaoFormaAcumulacao />
            </div>
          </div>
        </div>

        {calculoSelecionado === ID_TIPO_CALCULO_PONTUAL ? (
          <>
            <div className="ConfiguracaoMedida__vertical-divider" />
            <div className="ConfiguracaoMedida">
              <SelecaoDeflacao />
            </div>
          </>
        ) : null}
      </>
    );
  } else {
    return (
      <>
        <SelecaoPeriodoIndicadoresFinanceiros
          exibirTipoCalculo={exibirTipoCalculo}
        />
        <div className="ConfiguracaoMedida__vertical-divider" />
        <div className="ConfiguracaoMedida">
          <h3 className="ConfiguracaoMedida__group-title">
            <Translate>configuracaoRelatorio.parametros</Translate>
          </h3>
          <div className="ConfiguracaoMedida__group--grey">
            <div className="ConfiguracaoMedida__box">
              <SelecaoTipoConta />
            </div>
            <div className="ConfiguracaoMedida__box">
              <SelecaoVersaoConta />
            </div>
            <div className="ConfiguracaoMedida__box">
              <SelecaoFormaAcumulacao />
            </div>
          </div>
        </div>

        <div className="ConfiguracaoMedida__vertical-divider" />
        <SelecaoDeflacao />

        <div className="ConfiguracaoMedida__vertical-divider" />
        <SelecaoCasasDecimais />
      </>
    );
  }
}
